import {
  OpenInNew as OpenInNewIcon,
} from "@mui/icons-material";
import {
  Container,
  Typography,
  Box,
  Card,
  Button,
  Chip,
  Stack,
  CircularProgress,
  Paper,
} from "@mui/material";
import React, { useState, useEffect, useCallback } from "react";
import {
  TimeFilter,
  loadRecentFitsWithImages,
  getFilterLabel,
  getFitCount,
  openShopLink,
} from "./recent-fits.service";
import { useAuth } from "../../contexts/AuthContext";
import { RecentFits as RecentFitsType } from "../../types";

const RecentFits: React.FC = () => {
  const { user } = useAuth();
  const [recentFits, setRecentFits] = useState<RecentFitsType[]>([]);
  const [fitImages, setFitImages] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [activeFilter, setActiveFilter] = useState<TimeFilter>("all");

  const loadRecentFits = useCallback(async (filter: TimeFilter = "all") => {
    if (!user?.uid) {
      return;
    }

    try {
      setLoading(true);
      const { fits, images } = await loadRecentFitsWithImages(user.uid, filter, 50);

      setRecentFits(fits);
      setFitImages(images);
    } catch (error) {
      console.error("Error loading recent fits:", error);
      setRecentFits([]);
      setFitImages({});
    } finally {
      setLoading(false);
    }
  }, [user?.uid]);

  useEffect(() => {
    loadRecentFits(activeFilter);
  }, [loadRecentFits, activeFilter]);

  const handleFilterChange = (filter: TimeFilter) => {
    setActiveFilter(filter);
  };

  const handleShopLinkClick = (shopLink: string) => {
    openShopLink(shopLink);
  };

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
          Recent Fits
        </Typography>
        <Typography variant="body1" color="text.secondary">
          View and manage your fit visualizations
        </Typography>
      </Box>

      {/* Time Filter Chips */}
      <Box sx={{ mb: 3 }}>
        <Stack direction="row" spacing={1} flexWrap="wrap">
          {(["week", "month", "year", "all"] as TimeFilter[]).map((filter) => (
            <Chip
              key={filter}
              label={`${getFilterLabel(filter)} (${loading ? "..." : getFitCount(recentFits, filter)})`}
              variant={activeFilter === filter ? "filled" : "outlined"}
              color={activeFilter === filter ? "primary" : "default"}
              size="small"
              onClick={() => handleFilterChange(filter)}
              sx={{
                mb: 1,
                fontWeight: activeFilter === filter ? 600 : 400,
              }}
            />
          ))}
        </Stack>
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress size={48} />
        </Box>
      ) : recentFits.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: "center" }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No fits found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {activeFilter === "all"
              ? "You haven't tried any fits yet. Go to the Home page to create your first fit!"
              : `No fits found in the ${getFilterLabel(activeFilter).toLowerCase()}. Try a different time range.`
            }
          </Typography>
        </Paper>
      ) : (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "repeat(2, 1fr)",
              sm: "repeat(3, 1fr)",
              md: "repeat(4, 1fr)",
              lg: "repeat(6, 1fr)",
            },
            gap: 1.5,
          }}
        >
          {recentFits.map((fit, index) => (
            <Card
              key={fit.id}
              sx={{
                transition: "all 0.2s ease",
                "&:hover": {
                  transform: "translateY(-1px)",
                  boxShadow: 2,
                },
              }}
            >
              {/* Square Image Container */}
              <Box sx={{ position: "relative", width: "100%", paddingTop: "100%" }}>
                {fitImages[fit.id] ? (
                  <Box
                    component="img"
                    src={fitImages[fit.id]}
                    alt={`Fit #${recentFits.length - index}`}
                    sx={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: "100%",
                      bgcolor: "grey.200",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <CircularProgress size={24} />
                  </Box>
                )}
              </Box>

              <Box sx={{ p: 0.8, pb: 0.6 }}>
                <Stack spacing={0.125}>
                  {/* Date */}
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.65rem", mb: 0.25 }}>
                    {fit.createdAt.toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                    })}
                  </Typography>

                  {/* Go to Shop Button */}
                  <Button
                    variant="outlined"
                    size="small"
                    fullWidth
                    startIcon={<OpenInNewIcon sx={{ fontSize: 12 }} />}
                    onClick={() => handleShopLinkClick(fit.shopLink)}
                    sx={{
                      fontSize: "0.625rem",
                      py: 0.125,
                      minHeight: 20,
                    }}
                  >
                     Go to Shop
                  </Button>
                </Stack>
              </Box>
            </Card>
          ))}
        </Box>
      )}
    </Container>
  );
};

export default RecentFits;
